import cn from 'classnames';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Spinner } from '@features/campaigns/shared/components/Spinner/Spinner.tsx';
import { Advertisers } from '@features/users/adminPanel/components/Advertisers/Advertisers.tsx';
import formStyles from '@features/users/adminPanel/components/Form/Form.module.scss';
import { Form } from '@features/users/adminPanel/components/Form/Form.tsx';
import { FormFooter } from '@features/users/adminPanel/components/Form/FormFooter.tsx';
import { useAdvertisers } from '@features/users/adminPanel/hooks/useAdvertisers.tsx';
import { i18nNameSpace } from '@shared/consts/i18n.ts';

import styles from './AdvertisersSection.module.scss';

interface AdvertiserSectionProps {
  canFetchAdvertisers: boolean;
  isUserDataFetched: boolean;
}

export function AdvertisersSection({ canFetchAdvertisers, isUserDataFetched }: AdvertiserSectionProps) {
  const { t } = useTranslation([i18nNameSpace.USERS]);
  const [isAddAdvertiserModalOpen, setAddAdvertiserModalOpen] = useState(false);
  const handleAddAdvertiserModalToggle = () => {
    setAddAdvertiserModalOpen((prevState) => !prevState);
  };

  const {
    advertisersCollection,
    postNewAdvertiserMutation,
    postNewAdvertiserMutationIsPending,
    advertisersCollectionIsPending,
  } = useAdvertisers({
    onSuccessCallback: handleAddAdvertiserModalToggle,
    advertiserCollectionQueryEnabled: canFetchAdvertisers,
  });

  const showSpinner = !isUserDataFetched || (canFetchAdvertisers && advertisersCollectionIsPending);

  return (
    <section className={styles.wrapper}>
      <header className={styles.header}>
        <h2 className={styles.text}>{t('page.adminPanel.headers.advertisers')}</h2>
        <Button
          className={cn('p-button', styles.headerButton)}
          data-testid='add-advertiser-button'
          onClick={handleAddAdvertiserModalToggle}
          disabled={!canFetchAdvertisers}
        >
          <i className={cn('pi pi-plus', styles.headerButtonIcon)}></i>
          <span>{t('page.adminPanel.common.addAdvertiser')}</span>
        </Button>
      </header>

      {showSpinner ? (
        <div className={styles.spinnerWrapper}>
          <Spinner variant={'elementOverlay'} disableBackground />
        </div>
      ) : (
        <Advertisers data={advertisersCollection} />
      )}

      <Dialog
        visible={isAddAdvertiserModalOpen}
        onHide={handleAddAdvertiserModalToggle}
        draggable={false}
        resizable={false}
        header={t('page.adminPanel.common.addAdvertiser')}
      >
        <Form submitCallback={postNewAdvertiserMutation} className={formStyles.modalForm}>
          <FormFooter
            isPending={postNewAdvertiserMutationIsPending}
            dataTestId={'add-new-advertiser-submit'}
            cancelCallback={handleAddAdvertiserModalToggle}
          />
        </Form>
      </Dialog>
    </section>
  );
}
